import { Component, OnInit, signal, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ProductService } from '../../services/product.service';
import { Product } from '../../models/product.model';

@Component({
  selector: 'app-product-detail',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './product-detail.html',
  styleUrl: './product-detail.scss',
})
export class ProductDetail implements OnInit {
  product: Product | undefined;
  similarProducts: Product[] = [];
  isLoading = true;
  error = false;

  /** Image slider */
  currentImageIndex = signal(0);

  @ViewChild('similarTrack') similarTrackRef!: ElementRef<HTMLElement>;

  /** Accordion: 'description' | 'ingredients' | 'nutrition' */
  expandedSection = signal<string | null>('description');

  productImages: string[] = [];
  ratingStars: ('full' | 'half' | 'empty')[] = [];

  constructor(
    private route: ActivatedRoute,
    private productService: ProductService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.loadProduct(id);
      } else {
        this.error = true;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  loadProduct(id: string) {
    this.isLoading = true;
    this.error = false;
    this.currentImageIndex.set(0);
    this.expandedSection.set('description');
    this.productService.getProductById(id).subscribe({
      next: (product) => {
        try {
          this.product = product ?? undefined;
          if (!product) {
            this.error = true;
          } else {
            this.productImages = (product.images?.length ? product.images : [product.imageUrl]) as string[];
            this.calculateRatingStars(product.rating);
            this.loadSimilarProducts(product.id, product.category);
          }
        } catch (e) {
          console.error(e);
          this.error = true;
        } finally {
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.error = true;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private calculateRatingStars(rating: number | undefined | null) {
    const r = rating ?? 0;
    this.ratingStars = Array.from({ length: 5 }, (_, i) => {
      if (i < Math.floor(r)) return 'full';
      if (i < r) return 'half';
      return 'empty';
    });
  }

  setImage(index: number) {
    this.currentImageIndex.set(index);
  }

  prevImage() {
    const idx = this.currentImageIndex();
    this.currentImageIndex.set(idx <= 0 ? this.productImages.length - 1 : idx - 1);
  }

  nextImage() {
    const idx = this.currentImageIndex();
    this.currentImageIndex.set(idx >= this.productImages.length - 1 ? 0 : idx + 1);
  }

  selectSection(section: string) {
    this.expandedSection.set(section);
  }

  isSelected(section: string): boolean {
    return this.expandedSection() === section;
  }

  loadSimilarProducts(excludeId: string, category: string) {
    this.productService.getSimilarProducts(excludeId, category as import('../../models/product.model').ProductCategory, 8)
      .subscribe(products => this.similarProducts = products);
  }

  similarPrev() {
    const el = this.similarTrackRef?.nativeElement;
    if (el) el.scrollBy({ left: -300, behavior: 'smooth' });
  }

  similarNext() {
    const el = this.similarTrackRef?.nativeElement;
    if (el) el.scrollBy({ left: 300, behavior: 'smooth' });
  }

  getImgSrc(url: string | undefined): string {
    if (!url) return '';
    return url.startsWith('/uploads/') ? `https://dev.thehuskbakery.com/api${url}` : url;
  }
}
